import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Phone, MessageCircle, Mail, MapPin, ArrowUpRight, ArrowRight } from "lucide-react";
import { SITE } from "@/data/site";
import { APPLICATIONS } from "@/data/applications";
import { PRODUCTS } from "@/data/products";
import { Reveal } from "@/components/Reveal";

const inputCls =
  "w-full bg-[#FFFFFF] border border-[#334772]/15 px-4 py-3.5 text-sm text-[#334772] placeholder-[#9C9C9C] focus:outline-none focus:ring-2 focus:ring-[#334772] focus:border-transparent transition-shadow duration-200";

export default function EnquiryPage() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", country: "", interest: "General Enquiry", message: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const enquiryText = () =>
    `New RFQ / Enquiry\n\nName: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company || "-"}\nPhone: ${form.phone || "-"}\nCountry: ${form.country || "-"}\nRequirement: ${form.interest}\n\nSpecification:\n${form.message}`;

  const submit = (e) => {
    e.preventDefault();
    window.open(`https://wa.me/918554985323?text=${encodeURIComponent(enquiryText())}`, "_blank");
    toast.success("WhatsApp opened with your enquiry — just press send to reach our engineers.");
  };

  const mailtoHref = `mailto:${SITE.email}?subject=${encodeURIComponent(`RFQ: ${form.interest} - ${form.name || "New Enquiry"}`)}&body=${encodeURIComponent(enquiryText())}`;

  return (
    <>
      <Helmet>
        <title>Send RFQ / Enquiry | MESCO Springs — Custom Spring Manufacturer India</title>
        <meta name="description" content="Request a quotation for custom springs: compression, extension, torsion, heavy duty and exotic material springs. RFQ response within 24 hours. MESCO Springs Mumbai, exporting worldwide." />
      </Helmet>
      <div className="pt-40 pb-16 border-b border-[#334772]/15 blueprint-grid" data-testid="enquiry-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="font-mono-spec text-[11px] tracking-[0.35em] uppercase text-[#334772]">[ RFQ / ENQUIRY ]</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl uppercase leading-[0.9] text-[#334772] mt-6">
              Have a drawing?<br />We have the wire.
            </h1>
          </Reveal>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-7">
          <form onSubmit={submit} className="border border-[#334772]/15 bg-[#F3F5F8] p-8 lg:p-10" data-testid="enquiry-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="enq-name" className="block font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#706F70] mb-2">Name *</label>
                <input id="enq-name" data-testid="enquiry-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
              </div>
              <div>
                <label htmlFor="enq-email" className="block font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#706F70] mb-2">Email *</label>
                <input id="enq-email" data-testid="enquiry-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
              </div>
              <div>
                <label htmlFor="enq-company" className="block font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#706F70] mb-2">Company</label>
                <input id="enq-company" data-testid="enquiry-company" value={form.company} onChange={set("company")} className={inputCls} placeholder="Company / OEM" />
              </div>
              <div>
                <label htmlFor="enq-phone" className="block font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#706F70] mb-2">Phone</label>
                <input id="enq-phone" data-testid="enquiry-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91 ..." />
              </div>
              <div>
                <label htmlFor="enq-country" className="block font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#706F70] mb-2">Country</label>
                <input id="enq-country" data-testid="enquiry-country" value={form.country} onChange={set("country")} className={inputCls} placeholder="India / UAE / Germany ..." />
              </div>
              <div>
                <label htmlFor="enq-interest" className="block font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#706F70] mb-2">Requirement</label>
                <select id="enq-interest" data-testid="enquiry-interest" value={form.interest} onChange={set("interest")} className={inputCls}>
                  <option>General Enquiry</option>
                  {PRODUCTS.map((p) => (
                    <option key={p.slug}>{p.nav}</option>
                  ))}
                  {APPLICATIONS.map((a) => (
                    <option key={a.slug}>Springs for {a.nav}</option>
                  ))}
                  <option>Exotic Material Springs (Inconel / Hastelloy / Monel / 17-7PH)</option>
                </select>
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="enq-message" className="block font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#706F70] mb-2">Specification / Message *</label>
              <textarea id="enq-message" data-testid="enquiry-message" required rows={6} value={form.message} onChange={set("message")} className={inputCls} placeholder="Wire dia, OD, free length, load at height, material, quantity — or describe the application." />
            </div>
            <div className="mt-5">
              <span className="block font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#706F70] mb-2">Drawings & Specs</span>
              <p className="border border-dashed border-[#334772]/25 px-4 py-4 text-sm text-[#706F70]">
                Attach drawings right inside WhatsApp or email after you press send — PDF, STEP, DWG, photos all welcome.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <button
                type="submit"
                data-testid="enquiry-submit"
                className="inline-flex items-center gap-3 px-10 py-4 bg-[#334772] text-white text-sm font-bold uppercase tracking-[0.15em] hover:bg-[#2A3C60] transition-colors duration-200"
              >
                Send via WhatsApp
              </button>
              <a href={mailtoHref} data-testid="enquiry-mailto" className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-[#334772] hover:text-[#706F70] transition-colors duration-200">
                Or send by email <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
            <p className="mt-4 font-mono-spec text-[10px] tracking-widest uppercase text-[#9C9C9C]">
              Attach drawings in WhatsApp or email — our engineers reply within 24 hours.
            </p>
          </form>
        </Reveal>

        <aside className="lg:col-span-5 space-y-6">
          <Reveal delay={0.05}>
            <div className="border border-[#334772]/15 bg-[#FFFFFF] p-8">
              <h2 className="font-display text-3xl uppercase tracking-wide text-[#334772] mb-6">Talk to an engineer</h2>
              <ul className="space-y-5">
                <li>
                  <a href={SITE.phone1Href} data-testid="enquiry-call" className="flex items-center gap-4 group">
                    <span className="w-11 h-11 flex items-center justify-center bg-[#334772] text-white group-hover:bg-[#2A3C60] transition-colors"><Phone className="w-5 h-5" /></span>
                    <span>
                      <span className="block font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#9C9C9C]">Call</span>
                      <span className="text-[#334772] group-hover:text-[#706F70] transition-colors">{SITE.phone1} · {SITE.phone2}</span>
                    </span>
                  </a>
                </li>
                <li>
                  <a href={SITE.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="enquiry-whatsapp" className="flex items-center gap-4 group">
                    <span className="w-11 h-11 flex items-center justify-center bg-[#25D366] text-white"><MessageCircle className="w-5 h-5" /></span>
                    <span>
                      <span className="block font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#9C9C9C]">WhatsApp</span>
                      <span className="text-[#334772] group-hover:text-[#25D366] transition-colors">Chat with our sales desk</span>
                    </span>
                  </a>
                </li>
                <li>
                  <a href={`mailto:${SITE.email}`} data-testid="enquiry-email-link" className="flex items-center gap-4 group">
                    <span className="w-11 h-11 flex items-center justify-center border border-[#334772]/25 text-[#334772] group-hover:border-[#334772] transition-colors"><Mail className="w-5 h-5" /></span>
                    <span>
                      <span className="block font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#9C9C9C]">Email</span>
                      <span className="text-[#334772] group-hover:text-[#706F70] transition-colors">{SITE.email}</span>
                    </span>
                  </a>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-11 h-11 flex items-center justify-center border border-[#334772]/25 text-[#334772]"><MapPin className="w-5 h-5" /></span>
                  <span>
                    <span className="block font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#9C9C9C]">Works</span>
                    <span className="text-sm text-[#706F70] leading-relaxed">{SITE.address}</span>
                  </span>
                </li>
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <a href={SITE.mainSite} target="_blank" rel="noopener noreferrer" data-testid="enquiry-mainsite-card" className="group block border border-[#334772]/40 bg-[#334772]/5 p-8 hover:bg-[#334772]/10 transition-colors">
              <span className="font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#334772]">Main website</span>
              <h3 className="font-display text-3xl uppercase tracking-wide text-[#334772] mt-2 flex items-center gap-3">
                mescosprings.com <ArrowUpRight className="w-6 h-6 text-[#334772] transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </h3>
              <p className="mt-2 text-sm text-[#706F70]">Full company profile, client list, plant and process details.</p>
            </a>
          </Reveal>
        </aside>
      </div>
    </>
  );
}
